import React, { useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { DailyEngagementTrend } from "@/redux/services/dashboard/dashboard.type";
import { Skeleton } from "@/components/ui/skeleton";
import {
  dashboardColors,
  dashboardShadows,
  dashboardSpacing,
} from "@/lib/theme";

interface EngagementTrendsChartProps {
  data: DailyEngagementTrend[];
  previousData?: DailyEngagementTrend[];
  loading?: boolean;
}

export const EngagementTrendsChart: React.FC<EngagementTrendsChartProps> = ({
  data,
  previousData,
  loading,
}) => {
  const [selectedMetric, setSelectedMetric] = useState<
    "both" | "views" | "visitors"
  >("both");
  if (loading) {
    return (
      <div
        className="rounded-lg"
        style={{
          backgroundColor: dashboardColors.bgCard,
          padding: dashboardSpacing.cardPadding,
          boxShadow: dashboardShadows.card,
        }}
      >
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-[300px] w-full" />
      </div>
    );
  }

  const series: ApexAxisChartSeries = [];

  if (selectedMetric === "both" || selectedMetric === "views") {
    series.push({
      name: "Vues de page",
      type: "line",
      data: data.map((item) => item.page_views),
    });
  }

  if (selectedMetric === "both" || selectedMetric === "visitors") {
    series.push({
      name: "Visiteurs uniques",
      type: "line",
      data: data.map((item) => item.unique_visitors),
    });
  }

  if (previousData) {
    if (selectedMetric === "both" || selectedMetric === "views") {
      series.push({
        name: "Vues (période préc.)",
        type: "line",
        data: previousData.map((item) => item.page_views),
      });
    }

    if (selectedMetric === "both" || selectedMetric === "visitors") {
      series.push({
        name: "Visiteurs (période préc.)",
        type: "line",
        data: previousData.map((item) => item.unique_visitors),
      });
    }
  }

  const options: ApexOptions = {
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "12px",
      fontFamily: "Inter, sans-serif",
    },
    colors: [
      dashboardColors.primary,
      dashboardColors.success,
      dashboardColors.primary + "80",
      dashboardColors.success + "80",
    ],
    chart: {
      fontFamily: "Inter, sans-serif",
      type: "line",
      height: 350,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      dropShadow: {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1,
      },
    },
    responsive: [
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: 300,
          },
          yaxis: {
            show: false,
          },
        },
      },
    ],
    stroke: {
      width: [3, 3, 2, 2],
      curve: "smooth",
      dashArray: [0, 0, 5, 5],
    },
    markers: {
      size: [4, 4, 0, 0],
      strokeWidth: 2,
      hover: {
        size: 6,
      },
    },
    grid: {
      borderColor: dashboardColors.border,
      strokeDashArray: 3,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    xaxis: {
      categories: data.map((item) =>
        format(new Date(item.date), "dd MMM", { locale: fr })
      ),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: dashboardColors.textSecondary,
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      title: {
        text: "Nombre",
        style: {
          color: dashboardColors.textSecondary,
          fontSize: "12px",
          fontWeight: 500,
        },
      },
      labels: {
        style: {
          colors: dashboardColors.textSecondary,
          fontSize: "12px",
        },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: any) => {
        const date = data[dataPointIndex].date;
        const formattedDate = format(new Date(date), "dd MMMM yyyy", {
          locale: fr,
        });

        let tooltipHtml = `
          <div class="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
            <p class="font-medium text-gray-900 mb-2">${formattedDate}</p>
            <div class="space-y-1">
        `;

        if (selectedMetric === "both" || selectedMetric === "views") {
          tooltipHtml += `
            <div class="flex items-center justify-between gap-3 text-sm">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded" style="background-color: ${dashboardColors.primary}"></div>
                <span>Vues:</span>
              </div>
              <span class="font-medium">${data[dataPointIndex].page_views}</span>
            </div>
          `;
        }

        if (selectedMetric === "both" || selectedMetric === "visitors") {
          tooltipHtml += `
            <div class="flex items-center justify-between gap-3 text-sm">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded" style="background-color: ${dashboardColors.success}"></div>
                <span>Visiteurs uniques:</span>
              </div>
              <span class="font-medium">${data[dataPointIndex].unique_visitors}</span>
            </div>
          `;
        }

        if (previousData?.[dataPointIndex]) {
          if (selectedMetric === "both" || selectedMetric === "views") {
            tooltipHtml += `
              <div class="flex items-center justify-between gap-3 text-sm opacity-70">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded border-2 border-dashed" style="border-color: ${dashboardColors.primary}"></div>
                  <span>Vues (préc.):</span>
                </div>
                <span>${previousData[dataPointIndex].page_views}</span>
              </div>
            `;
          }

          if (selectedMetric === "both" || selectedMetric === "visitors") {
            tooltipHtml += `
              <div class="flex items-center justify-between gap-3 text-sm opacity-70">
                <div class="flex items-center gap-2">
                  <div class="w-3 h-3 rounded border-2 border-dashed" style="border-color: ${dashboardColors.success}"></div>
                  <span>Visiteurs (préc.):</span>
                </div>
                <span>${previousData[dataPointIndex].unique_visitors}</span>
              </div>
            `;
          }
        }

        tooltipHtml += `
            </div>
          </div>
        `;

        return tooltipHtml;
      },
    },
  };

  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: dashboardColors.bgCard,
        padding: dashboardSpacing.cardPadding,
        boxShadow: dashboardShadows.card,
      }}
    >
      <div className="flex justify-between items-center mb-4">
        <h3
          className="text-lg font-semibold"
          style={{ color: dashboardColors.textPrimary }}
        >
          Tendances d'engagement client
        </h3>
        <div className="flex gap-2">
          <button
            onClick={() => setSelectedMetric("both")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "both"
                ? "bg-orange-100 text-orange-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Tout
          </button>
          <button
            onClick={() => setSelectedMetric("views")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "views"
                ? "bg-orange-100 text-orange-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Vues
          </button>
          <button
            onClick={() => setSelectedMetric("visitors")}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              selectedMetric === "visitors"
                ? "bg-green-100 text-green-600"
                : "bg-gray-100 text-gray-600"
            }`}
          >
            Visiteurs
          </button>
        </div>
      </div>
      <div id="engagementTrendsChart">
        <ReactApexChart
          options={options}
          series={series}
          type="line"
          height={350}
        />
      </div>
    </div>
  );
};
